import { useState, useMemo } from 'react';
import { useAdminClientePerfil, getColorHex } from '../../../hooks/useAdminClientePerfil';
import type { Prenda } from '../../../hooks/useClienteArmario';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';
import { supabase } from '../../../lib/supabase';
import { generarLooksSugeridos, type LookSugerido } from '../../../lib/looksSugeridos';

interface LooksSugeridosProps {
  clienteId: number;
  prendas: Prenda[];
  calendario: LookCalendario[];
  onAddToCalendar: (look: Omit<LookCalendario, 'id'>) => void;
}

export default function LooksSugeridos({ clienteId, prendas, calendario, onAddToCalendar }: LooksSugeridosProps) {
  const { perfil: adminPerfil, setPerfil } = useAdminClientePerfil(clienteId);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [filterOcasion, setFilterOcasion] = useState<string>('');
  const [favFilter, setFavFilter] = useState<'todos' | 'favoritos'>('todos');
  const [savingFav, setSavingFav] = useState<string | null>(null);

  const favoritos = useMemo(() => adminPerfil?.looks_favoritos ?? [], [adminPerfil]);

  const looks = useMemo(() => {
    const paleta = adminPerfil?.paleta_colores ?? [];
    const ocasiones = adminPerfil?.ocasiones_preferidas ?? ['Diario'];
    return generarLooksSugeridos(prendas, paleta, ocasiones);
  }, [prendas, adminPerfil]);

  const ocasionesUnicas = useMemo(() => {
    const set = new Set<string>();
    looks.forEach((l) => set.add(l.ocasion));
    return Array.from(set);
  }, [looks]);

  const filteredLooks = useMemo(() => {
    let result = looks;
    if (filterOcasion) {
      result = result.filter((l) => l.ocasion === filterOcasion);
    }
    if (favFilter === 'favoritos') {
      result = result.filter((l) => favoritos.includes(l.id));
    }
    return result;
  }, [looks, filterOcasion, favFilter, favoritos]);

  const handleToggleFavorito = async (lookId: string) => {
    setSavingFav(lookId);
    const nextFavoritos = favoritos.includes(lookId)
      ? favoritos.filter((f) => f !== lookId)
      : [...favoritos, lookId];

    try {
      const { data, error } = await supabase
        .from('perfiles_cliente')
        .update({ looks_favoritos: nextFavoritos, updated_at: new Date().toISOString() })
        .eq('cliente_id', clienteId)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPerfil((prev) => (prev ? { ...prev, looks_favoritos: nextFavoritos } : null));
      }
    } catch {
      // silently fail
    } finally {
      setSavingFav(null);
    }
  };

  const handleUsarLook = (look: LookSugerido) => {
    const prendaIds = look.prendas.map((p) => p.id);
    const todayStr = new Date().toISOString().split('T')[0];

    onAddToCalendar({
      cliente_id: clienteId,
      fecha: todayStr,
      titulo: look.nombre,
      notas: `Look sugerido generado automáticamente para ${look.ocasion}. Prendas: ${look.prendas.map((p) => p.nombre).join(', ')}`,
      prendas_ids: prendaIds,
      ocasion: look.ocasion,
      estacion: look.temporada,
      estado: 'planeado',
    });

    setAddedIds((prev) => new Set([...prev, look.id]));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">Looks sugeridos</h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            Combinaciones automáticas basadas en tu paleta de colores y ocasiones preferidas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Favoritos filter */}
          <div className="flex items-center gap-1 bg-brand-gray-100 rounded-lg p-1">
            <button
              onClick={() => setFavFilter('todos')}
              className={`px-3 py-1.5 rounded-md font-sans text-sm transition-all ${
                favFilter === 'todos'
                  ? 'bg-brand-black text-white'
                  : 'text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFavFilter('favoritos')}
              className={`px-3 py-1.5 rounded-md font-sans text-sm transition-all flex items-center gap-1 ${
                favFilter === 'favoritos'
                  ? 'bg-brand-black text-white'
                  : 'text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              <i className="ri-heart-3-fill text-red-500" />
              Favoritos{favoritos.length > 0 ? ` (${favoritos.length})` : ''}
            </button>
          </div>
          {ocasionesUnicas.length > 0 && (
            <select
              value={filterOcasion}
              onChange={(e) => setFilterOcasion(e.target.value)}
              className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
            >
              <option value="">Todas las ocasiones</option>
              {ocasionesUnicas.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Paleta de colores del perfil */}
      {adminPerfil?.paleta_colores && adminPerfil.paleta_colores.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 flex items-center justify-center text-brand-sage">
              <i className="ri-palette-line" />
            </div>
            <p className="font-sans text-brand-black text-xs font-medium uppercase tracking-wider">
              Paleta de colores que define Anthony para ti
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {adminPerfil.paleta_colores.map((color) => {
              const hex = getColorHex(color);
              return (
                <span
                  key={color}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs"
                  style={{ backgroundColor: hex + '20' }}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  {color}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista de looks sugeridos */}
      {filteredLooks.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredLooks.map((look) => {
            const isAdded = addedIds.has(look.id);
            const isFav = favoritos.includes(look.id);
            return (
              <div
                key={look.id}
                className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col transition-shadow hover:shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-brand-black text-lg font-medium">{look.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-brand-sage/20 font-sans text-[11px] text-brand-black">
                        {look.ocasion}
                      </span>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-brand-gray-100 font-sans text-[11px] text-brand-gray-600">
                        {look.temporada}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Favorite toggle */}
                    <button
                      onClick={() => handleToggleFavorito(look.id)}
                      disabled={savingFav === look.id}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                        isFav
                          ? 'bg-red-50 text-red-500 hover:bg-red-100'
                          : 'bg-brand-gray-100 text-brand-gray-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                      title={isFav ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                    >
                      {savingFav === look.id ? (
                        <i className="ri-loader-4-line animate-spin text-sm" />
                      ) : (
                        <i className={isFav ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'} />
                      )}
                    </button>
                    <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
                      <i className="ri-shirt-line text-lg" />
                    </div>
                  </div>
                </div>

                {/* Collage de prendas */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {look.prendas.map((prenda) => (
                    <div
                      key={prenda.id}
                      className="relative rounded-xl overflow-hidden bg-brand-gray-50 aspect-[3/4]"
                    >
                      {prenda.imagen_url ? (
                        <img
                          src={prenda.imagen_url}
                          alt={prenda.nombre}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-gray-300">
                          <i className="ri-t-shirt-line text-2xl" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="font-sans text-white text-xs font-medium truncate">{prenda.nombre}</p>
                        <p className="font-sans text-white/70 text-[10px]">{prenda.categoria}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Lista de prendas con colores */}
                <div className="flex-1 space-y-2 mb-4">
                  {look.prendas.map((prenda) => {
                    const hex = getColorHex(prenda.color);
                    return (
                      <div key={prenda.id} className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                          style={{ backgroundColor: hex }}
                        />
                        <span className="font-sans text-sm text-brand-gray-700 truncate">
                          {prenda.nombre}
                        </span>
                        <span className="font-sans text-[11px] text-brand-gray-400 ml-auto shrink-0">
                          {prenda.categoria}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Botón de acción */}
                <div className="mt-auto pt-3 border-t border-brand-gray-200">
                  {isAdded ? (
                    <div className="flex items-center justify-center gap-2 text-brand-sage font-sans text-sm font-medium py-2.5">
                      <i className="ri-check-line" />
                      Añadido al calendario
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUsarLook(look)}
                      className="w-full flex items-center justify-center gap-2 bg-brand-black text-white font-sans text-sm px-4 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-calendar-check-line" />
                      Planificar look
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-t-shirt-air-line text-2xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            {favFilter === 'favoritos'
              ? 'No tienes looks favoritos aún. Marca con el corazón los que más te gusten.'
              : prendas.filter((p) => p.activa).length === 0
              ? 'Tu armario está vacío. Añade prendas para generar looks sugeridos.'
              : 'No hay suficientes prendas que coincidan con tu paleta de colores.'}
          </p>
          <p className="font-sans text-brand-gray-400 text-xs mt-2">
            {favFilter === 'favoritos'
              ? 'Ve a "Todos" para ver y marcar looks.'
              : 'Ve a "Mi Armario" para registrar más prendas o espera a que Anthony defina tu paleta.'}
          </p>
        </div>
      )}

      {/* Stats abajo */}
      {looks.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 text-center">
            <p className="font-serif text-brand-black text-2xl font-light">{looks.length}</p>
            <p className="font-sans text-brand-gray-500 text-xs mt-1">Looks disponibles</p>
          </div>
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 text-center">
            <p className="font-serif text-brand-black text-2xl font-light">{addedIds.size}</p>
            <p className="font-sans text-brand-gray-500 text-xs mt-1">Looks planificados</p>
          </div>
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 text-center">
            <p className="font-serif text-brand-black text-2xl font-light">{favoritos.length}</p>
            <p className="font-sans text-brand-gray-500 text-xs mt-1">Favoritos</p>
          </div>
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 text-center">
            <p className="font-serif text-brand-black text-2xl font-light">{ocasionesUnicas.length}</p>
            <p className="font-sans text-brand-gray-500 text-xs mt-1">Ocasiones cubiertas</p>
          </div>
        </div>
      )}
    </div>
  );
}
